'use client';

import { c as _c } from "react/compiler-runtime";
import { useMemo } from 'react';
import { useSearchParams } from 'next/navigation';
import { flattenPortfolioAttribution, resolvePortfolioAttribution } from '@/features/portfolio/_utils/resolve-portfolio-attribution';
import { useUtmParams } from '@/hooks/use-utm-params';
import { Analytics } from '@/lib/analytics';
import { type AccountCreationMethod, AUTH_EVENTS, type AuthEventBaseProperties, type AuthFlow, type AuthFunnelStep, type AuthStrategy, type SignUpChoiceClickedProperties } from '@/lib/analytics/auth-events';
import { getAnonymousCorrelationId } from '@/lib/analytics/get-anonymous-correlation-id';
import { pushGa4UserId } from '@/lib/analytics/push-ga4-user-id';
import { useAnalytics } from '@/lib/analytics/use-analytics';
import type { FeatureFlagVariant } from '@/lib/feature-flags/types';
import { getAllowlistedSearchParams } from '@/utils/get-allowlisted-search-params';
import { usePasswordlessSignUpVariant } from './use-passwordless-sign-up-variant';
type AuthEventProperties = AuthEventBaseProperties & Record<string, unknown>;
type AuthReturnScreen = 'start' | 'start_email_form' | 'email_verification' | 'verifications' | 'choose_strategy' | 'forgot_password' | 'reset_password';
interface IdentifyAuthUserParams {
  accountCreationMethod?: AccountCreationMethod;
  email?: string;
  firstName?: string | null;
  lastName?: string | null;
  newsletter?: boolean;
  userId?: string;
}
function buildBaseProperties(passwordlessVariant: FeatureFlagVariant | undefined, utm: {
  campaign: string;
  content: string;
  medium: string;
  source: string;
  referrer: string;
  partner: string;
}, allowlistedParams: string, anonymousId: string, attributionProps: ReturnType<typeof flattenPortfolioAttribution>, overrides?: Partial<AuthEventProperties>): AuthEventProperties {
  const params = allowlistedParams ? Object.fromEntries(new URLSearchParams(allowlistedParams).entries()) : undefined;
  return {
    ...(passwordlessVariant !== undefined && {
      ab_test_passwordless_sign_up_2026_q3: passwordlessVariant
    }),
    ...{
      anonymous_id: anonymousId ?? ''
    },
    ...(utm.campaign && {
      utm_campaign: utm.campaign
    }),
    ...(utm.content && {
      utm_content: utm.content
    }),
    ...(utm.medium && {
      utm_medium: utm.medium
    }),
    ...(utm.source && {
      utm_source: utm.source
    }),
    ...(utm.referrer && {
      utm_referrer: utm.referrer
    }),
    ...(utm.partner && {
      partner: utm.partner
    }),
    ...attributionProps,
    ...(params && Object.keys(params).length > 0 && {
      search_params: params
    }),
    ...overrides
  };
}
export function useAuthEvents() {
  const $ = _c(163);
  const searchParams = useSearchParams();
  const {
    track
  } = useAnalytics();
  const {
    variant: passwordlessVariant
  } = usePasswordlessSignUpVariant();
  const {
    campaign,
    content,
    medium,
    source,
    referrer,
    partner,
    portfolioCode,
    portfolioUrl,
    clickIdFallbacks
  } = useUtmParams();
  let t0;
  if ($[0] !== clickIdFallbacks || $[1] !== searchParams) {
    t0 = getAllowlistedSearchParams(searchParams, clickIdFallbacks);
    $[0] = clickIdFallbacks;
    $[1] = searchParams;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const allowlistedParams = t0;
  let t1;
  if ($[3] !== content || $[4] !== portfolioCode || $[5] !== portfolioUrl) {
    t1 = resolvePortfolioAttribution({
      portfolioCode,
      portfolioUrl,
      utmContent: content,
      referrer: null
    });
    $[3] = content;
    $[4] = portfolioCode;
    $[5] = portfolioUrl;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const portfolioAttribution = t1;
  let t2;
  if ($[7] !== portfolioAttribution) {
    t2 = flattenPortfolioAttribution(portfolioAttribution);
    $[7] = portfolioAttribution;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  const attributionProps = t2;
  let t3;
  if ($[9] !== allowlistedParams || $[10] !== attributionProps || $[11] !== campaign || $[12] !== content || $[13] !== medium || $[14] !== partner || $[15] !== passwordlessVariant || $[16] !== referrer || $[17] !== source) {
    t3 = overrides => buildBaseProperties(passwordlessVariant, {
      campaign,
      content,
      medium,
      source,
      referrer,
      partner
    }, allowlistedParams, getAnonymousCorrelationId() ?? "", attributionProps, overrides);
    $[9] = allowlistedParams;
    $[10] = attributionProps;
    $[11] = campaign;
    $[12] = content;
    $[13] = medium;
    $[14] = partner;
    $[15] = passwordlessVariant;
    $[16] = referrer;
    $[17] = source;
    $[18] = t3;
  } else {
    t3 = $[18];
  }
  const baseProps = t3;
  let t4;
  if ($[19] !== track) {
    t4 = (event, properties, callback) => {
      track(event, properties, callback);
    };
    $[19] = track;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  const trackAuthEvent = t4;
  let t5;
  if ($[21] !== baseProps || $[22] !== trackAuthEvent) {
    t5 = (flow, currentScreen, step, extra) => {
      trackAuthEvent(AUTH_EVENTS.AUTH_SCREEN_VIEWED, baseProps({
        flow,
        step,
        current_screen: currentScreen,
        ...extra
      }));
    };
    $[21] = baseProps;
    $[22] = trackAuthEvent;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  const trackAuthScreenViewed = t5;
  let t6;
  if ($[24] !== baseProps) {
    t6 = t7 => {
      const {
        userId,
        email,
        firstName,
        lastName,
        newsletter,
        accountCreationMethod
      } = t7;
      if (userId) {
        pushGa4UserId(userId);
      }
      Analytics.identify(userId, baseProps({
        ...(email && {
          email
        }),
        first_name: firstName ?? "",
        last_name: lastName ?? "",
        ...(newsletter !== undefined && {
          newsletter
        }),
        ...(accountCreationMethod && {
          account_creation_method: accountCreationMethod
        })
      }));
    };
    $[24] = baseProps;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  const identifyAuthUser = t6;
  let t7;
  if ($[26] !== baseProps || $[27] !== trackAuthEvent) {
    t7 = (flow_0, step_0, errorCode, properties_0) => {
      trackAuthEvent(AUTH_EVENTS.AUTH_ERROR, baseProps({
        flow: flow_0,
        step: step_0,
        result: "error",
        ...(errorCode && {
          error_code: errorCode
        }),
        ...properties_0
      }));
    };
    $[26] = baseProps;
    $[27] = trackAuthEvent;
    $[28] = t7;
  } else {
    t7 = $[28];
  }
  const trackAuthError = t7;
  let t8;
  if ($[29] !== baseProps || $[30] !== trackAuthEvent) {
    t8 = strategy => {
      trackAuthEvent(AUTH_EVENTS.SIGN_IN_STRATEGY_SELECTED, baseProps({
        flow: "sign_in",
        step: "choose_strategy",
        strategy,
        method: "email"
      }));
    };
    $[29] = baseProps;
    $[30] = trackAuthEvent;
    $[31] = t8;
  } else {
    t8 = $[31];
  }
  const trackSignInStrategySelected = t8;
  let t9;
  if ($[32] !== baseProps || $[33] !== trackAuthEvent) {
    t9 = (choice, properties_1) => {
      trackAuthEvent(AUTH_EVENTS.SIGN_IN_SESSION_SELECTED, baseProps({
        flow: "sign_in",
        step: "choose_session",
        choice,
        ...properties_1
      }));
    };
    $[32] = baseProps;
    $[33] = trackAuthEvent;
    $[34] = t9;
  } else {
    t9 = $[34];
  }
  const trackSignInSessionSelected = t9;
  let t10;
  if ($[35] !== baseProps || $[36] !== trackAuthEvent) {
    t10 = (choice_0, properties_2) => {
      trackAuthEvent(AUTH_EVENTS.SIGN_UP_SESSION_SELECTED, baseProps({
        flow: "sign_up",
        step: "choose_session",
        choice: choice_0,
        ...properties_2
      }));
    };
    $[35] = baseProps;
    $[36] = trackAuthEvent;
    $[37] = t10;
  } else {
    t10 = $[37];
  }
  const trackSignUpSessionSelected = t10;
  let t11;
  if ($[38] !== baseProps || $[39] !== trackAuthEvent) {
    t11 = () => {
      trackAuthEvent(AUTH_EVENTS.SIGN_IN_FORGOT_PASSWORD_OPENED, baseProps({
        flow: "sign_in",
        step: "verifications",
        method: "email"
      }));
    };
    $[38] = baseProps;
    $[39] = trackAuthEvent;
    $[40] = t11;
  } else {
    t11 = $[40];
  }
  const trackSignInForgotPasswordOpened = t11;
  let t12;
  if ($[41] !== baseProps || $[42] !== trackAuthEvent) {
    t12 = () => {
      trackAuthEvent(AUTH_EVENTS.SIGN_IN_RESET_CODE_REQUESTED, baseProps({
        flow: "sign_in",
        step: "forgot_password",
        method: "email"
      }));
    };
    $[41] = baseProps;
    $[42] = trackAuthEvent;
    $[43] = t12;
  } else {
    t12 = $[43];
  }
  const trackSignInResetCodeRequested = t12;
  let t13;
  if ($[44] !== baseProps || $[45] !== trackAuthEvent) {
    t13 = () => {
      trackAuthEvent(AUTH_EVENTS.SIGN_IN_RESET_CODE_SUBMITTED, baseProps({
        flow: "sign_in",
        step: "reset_password",
        method: "email"
      }));
    };
    $[44] = baseProps;
    $[45] = trackAuthEvent;
    $[46] = t13;
  } else {
    t13 = $[46];
  }
  const trackSignInResetCodeSubmitted = t13;
  let t14;
  if ($[47] !== baseProps || $[48] !== trackAuthEvent) {
    t14 = () => {
      trackAuthEvent(AUTH_EVENTS.SIGN_IN_RESET_CODE_RESENT, baseProps({
        flow: "sign_in",
        step: "reset_password",
        method: "email"
      }));
    };
    $[47] = baseProps;
    $[48] = trackAuthEvent;
    $[49] = t14;
  } else {
    t14 = $[49];
  }
  const trackSignInResetCodeResent = t14;
  let t15;
  if ($[50] !== baseProps || $[51] !== trackAuthEvent) {
    t15 = () => {
      trackAuthEvent(AUTH_EVENTS.SIGN_IN_NEW_PASSWORD_SUBMITTED, baseProps({
        flow: "sign_in",
        step: "reset_password",
        method: "email"
      }));
    };
    $[50] = baseProps;
    $[51] = trackAuthEvent;
    $[52] = t15;
  } else {
    t15 = $[52];
  }
  const trackSignInNewPasswordSubmitted = t15;
  let t16;
  if ($[53] !== baseProps || $[54] !== trackAuthEvent) {
    t16 = strategy_0 => {
      trackAuthEvent(AUTH_EVENTS.SIGN_IN_CODE_RESENT, baseProps({
        flow: "sign_in",
        step: "verifications",
        method: "email",
        ...(strategy_0 && {
          strategy: strategy_0
        })
      }));
    };
    $[53] = baseProps;
    $[54] = trackAuthEvent;
    $[55] = t16;
  } else {
    t16 = $[55];
  }
  const trackSignInCodeResent = t16;
  let t17;
  if ($[56] !== baseProps || $[57] !== trackAuthEvent) {
    t17 = (choice_1, newsletter_0) => {
      trackAuthEvent(AUTH_EVENTS.SIGN_UP_CHOICE_CLICKED, {
        ...baseProps({
          newsletter: newsletter_0,
          flow: "sign_up",
          step: "start"
        }),
        choice: choice_1
      });
    };
    $[56] = baseProps;
    $[57] = trackAuthEvent;
    $[58] = t17;
  } else {
    t17 = $[58];
  }
  const trackSignUpChoiceClicked = t17;
  let t18;
  if ($[59] !== baseProps || $[60] !== trackAuthEvent) {
    t18 = (currentScreen_0, redirectedScreen) => {
      trackAuthEvent(AUTH_EVENTS.SIGN_UP_EMAIL_RETURN_CLICKED, baseProps({
        flow: "sign_up",
        method: "email",
        current_screen: currentScreen_0,
        redirected_screen: redirectedScreen
      }));
    };
    $[59] = baseProps;
    $[60] = trackAuthEvent;
    $[61] = t18;
  } else {
    t18 = $[61];
  }
  const trackSignUpEmailReturnClicked = t18;
  let t19;
  if ($[62] !== baseProps || $[63] !== trackAuthEvent) {
    t19 = (accountCreationMethod_0, newsletter_1, passwordChosen) => {
      trackAuthEvent(AUTH_EVENTS.SIGN_UP_STARTED, baseProps({
        flow: "sign_up",
        step: "start",
        newsletter: newsletter_1,
        account_creation_method: accountCreationMethod_0,
        ...(passwordChosen === undefined ? {} : {
          password_chosen: passwordChosen
        })
      }));
    };
    $[62] = baseProps;
    $[63] = trackAuthEvent;
    $[64] = t19;
  } else {
    t19 = $[64];
  }
  const trackSignUpStarted = t19;
  let t20;
  if ($[65] !== baseProps || $[66] !== trackAuthEvent) {
    t20 = choice_2 => {
      trackAuthEvent(AUTH_EVENTS.SIGN_UP_PASSWORD_OPTION_TOGGLED, baseProps({
        flow: "sign_up",
        step: "start",
        method: "email",
        choice: choice_2
      }));
    };
    $[65] = baseProps;
    $[66] = trackAuthEvent;
    $[67] = t20;
  } else {
    t20 = $[67];
  }
  const trackSignUpPasswordOptionToggled = t20;
  let t21;
  if ($[68] !== baseProps || $[69] !== trackAuthEvent) {
    t21 = () => {
      trackAuthEvent(AUTH_EVENTS.SIGN_UP_EMAIL_CODE_SENT, baseProps({
        flow: "sign_up",
        step: "email_verification",
        method: "email",
        account_creation_method: "email"
      }));
    };
    $[68] = baseProps;
    $[69] = trackAuthEvent;
    $[70] = t21;
  } else {
    t21 = $[70];
  }
  const trackSignUpEmailCodeSent = t21;
  let t22;
  if ($[71] !== baseProps || $[72] !== trackAuthEvent) {
    t22 = () => {
      trackAuthEvent(AUTH_EVENTS.SIGN_UP_EMAIL_VERIFIED, baseProps({
        flow: "sign_up",
        step: "email_verification",
        method: "email",
        result: "success",
        account_creation_method: "email"
      }));
    };
    $[71] = baseProps;
    $[72] = trackAuthEvent;
    $[73] = t22;
  } else {
    t22 = $[73];
  }
  const trackSignUpEmailVerified = t22;
  let t23;
  if ($[74] !== baseProps || $[75] !== trackAuthEvent) {
    t23 = () => {
      trackAuthEvent(AUTH_EVENTS.SIGN_UP_EXISTING_ACCOUNT_HANDOFF, baseProps({
        flow: "sign_up",
        step: "start",
        method: "email"
      }));
    };
    $[74] = baseProps;
    $[75] = trackAuthEvent;
    $[76] = t23;
  } else {
    t23 = $[76];
  }
  const trackSignUpExistingAccountHandoff = t23;
  let t24;
  if ($[77] !== baseProps || $[78] !== trackAuthEvent) {
    t24 = () => {
      trackAuthEvent(AUTH_EVENTS.SIGN_UP_EMAIL_CODE_RESENT, baseProps({
        flow: "sign_up",
        step: "email_verification",
        method: "email",
        account_creation_method: "email"
      }));
    };
    $[77] = baseProps;
    $[78] = trackAuthEvent;
    $[79] = t24;
  } else {
    t24 = $[79];
  }
  const trackSignUpEmailCodeResent = t24;
  let t25;
  if ($[80] !== baseProps || $[81] !== trackAuthEvent) {
    t25 = (accountCreationMethod_1, hasSession) => {
      trackAuthEvent(AUTH_EVENTS.SIGN_UP_WEBSITE_CALLBACK_ARRIVED, baseProps({
        flow: "sign_up",
        step: "website_callback",
        ...(accountCreationMethod_1 && {
          account_creation_method: accountCreationMethod_1
        }),
        has_session: hasSession
      }));
    };
    $[80] = baseProps;
    $[81] = trackAuthEvent;
    $[82] = t25;
  } else {
    t25 = $[82];
  }
  const trackSignUpWebsiteCallbackArrived = t25;
  let t26;
  if ($[83] !== baseProps || $[84] !== trackAuthEvent) {
    t26 = () => {
      trackAuthEvent(AUTH_EVENTS.SSO_SIGN_UP_COMPLETED, baseProps({
        flow: "sign_up",
        step: "sso_callback",
        method: "google",
        account_creation_method: "google"
      }));
    };
    $[83] = baseProps;
    $[84] = trackAuthEvent;
    $[85] = t26;
  } else {
    t26 = $[85];
  }
  const trackSsoSignUpCompleted = t26;
  let t27;
  if ($[86] !== baseProps || $[87] !== trackAuthEvent) {
    t27 = () => {
      trackAuthEvent(AUTH_EVENTS.SSO_SIGN_IN_COMPLETED, baseProps({
        flow: "sign_in",
        step: "sso_callback",
        method: "google",
        account_creation_method: "google"
      }));
    };
    $[86] = baseProps;
    $[87] = trackAuthEvent;
    $[88] = t27;
  } else {
    t27 = $[88];
  }
  const trackSsoSignInCompleted = t27;
  let t28;
  if ($[89] !== baseProps || $[90] !== trackAuthEvent) {
    t28 = direction => {
      trackAuthEvent(AUTH_EVENTS.SSO_TRANSFER_USED, baseProps({
        flow: direction === "sign_up_to_sign_in" ? "sign_up" : "sign_in",
        step: "sso_callback",
        sso_transfer_direction: direction
      }));
    };
    $[89] = baseProps;
    $[90] = trackAuthEvent;
    $[91] = t28;
  } else {
    t28 = $[91];
  }
  const trackSsoTransferUsed = t28;
  let t29;
  if ($[92] !== baseProps || $[93] !== trackAuthEvent) {
    t29 = () => {
      trackAuthEvent(AUTH_EVENTS.SSO_EXISTING_SESSION_REUSED, baseProps({
        step: "sso_callback"
      }));
    };
    $[92] = baseProps;
    $[93] = trackAuthEvent;
    $[94] = t29;
  } else {
    t29 = $[94];
  }
  const trackSsoExistingSessionReused = t29;
  let t30;
  if ($[95] !== baseProps || $[96] !== trackAuthEvent) {
    t30 = () => {
      trackAuthEvent(AUTH_EVENTS.SSO_FALLBACK_TO_SIGN_IN, baseProps({
        flow: "sign_in",
        step: "sso_callback"
      }));
    };
    $[95] = baseProps;
    $[96] = trackAuthEvent;
    $[97] = t30;
  } else {
    t30 = $[97];
  }
  const trackSsoFallbackToSignIn = t30;
  let t31;
  if ($[98] !== baseProps || $[99] !== trackAuthEvent) {
    t31 = () => {
      trackAuthEvent(AUTH_EVENTS.SSO_FALLBACK_TO_SIGN_UP, baseProps({
        flow: "sign_up",
        step: "sso_callback"
      }));
    };
    $[98] = baseProps;
    $[99] = trackAuthEvent;
    $[100] = t31;
  } else {
    t31 = $[100];
  }
  const trackSsoFallbackToSignUp = t31;
  let t32;
  if ($[101] !== baseProps || $[102] !== trackAuthEvent) {
    t32 = (recovery, properties_3) => {
      trackAuthEvent(AUTH_EVENTS.SSO_SIGN_UP_RECOVERY_STARTED, baseProps({
        flow: "sign_up",
        step: "sso_callback",
        method: "google",
        sso_recovery: recovery,
        ...properties_3
      }));
    };
    $[101] = baseProps;
    $[102] = trackAuthEvent;
    $[103] = t32;
  } else {
    t32 = $[103];
  }
  const trackSsoSignUpRecoveryStarted = t32;
  let t33;
  if ($[104] !== baseProps || $[105] !== trackAuthEvent) {
    t33 = accountCreationMethod_2 => {
      trackAuthEvent(AUTH_EVENTS.SIGN_IN_STARTED, baseProps({
        flow: "sign_in",
        step: "start",
        account_creation_method: accountCreationMethod_2
      }));
    };
    $[104] = baseProps;
    $[105] = trackAuthEvent;
    $[106] = t33;
  } else {
    t33 = $[106];
  }
  const trackSignInStarted = t33;
  let t34;
  if ($[107] !== baseProps || $[108] !== trackAuthEvent) {
    t34 = (currentScreen_1, redirectedScreen_0) => {
      trackAuthEvent(AUTH_EVENTS.SIGN_IN_EMAIL_RETURN_CLICKED, baseProps({
        flow: "sign_in",
        method: "email",
        current_screen: currentScreen_1,
        redirected_screen: redirectedScreen_0
      }));
    };
    $[107] = baseProps;
    $[108] = trackAuthEvent;
    $[109] = t34;
  } else {
    t34 = $[109];
  }
  const trackSignInEmailReturnClicked = t34;
  let t35;
  if ($[110] !== baseProps || $[111] !== trackAuthEvent) {
    t35 = strategy_1 => {
      trackAuthEvent(AUTH_EVENTS.SIGN_IN_MFA_STARTED, baseProps({
        flow: "sign_in",
        step: "verifications",
        method: "email",
        strategy: strategy_1
      }));
    };
    $[110] = baseProps;
    $[111] = trackAuthEvent;
    $[112] = t35;
  } else {
    t35 = $[112];
  }
  const trackSignInMfaStarted = t35;
  let t36;
  if ($[113] !== baseProps || $[114] !== trackAuthEvent) {
    t36 = t37 => {
      const strategy_2 = t37 === undefined ? "email_code" : t37;
      trackAuthEvent(AUTH_EVENTS.SIGN_IN_MFA_COMPLETED, baseProps({
        flow: "sign_in",
        step: "verifications",
        method: "email",
        strategy: strategy_2
      }));
    };
    $[113] = baseProps;
    $[114] = trackAuthEvent;
    $[115] = t36;
  } else {
    t36 = $[115];
  }
  const trackSignInMfaCompleted = t36;
  let t37;
  if ($[116] !== baseProps || $[117] !== trackAuthEvent) {
    t37 = () => {
      trackAuthEvent(AUTH_EVENTS.SIGN_IN_MFA_BACKUP_CODE_SELECTED, baseProps({
        flow: "sign_in",
        step: "verifications",
        method: "email"
      }));
    };
    $[116] = baseProps;
    $[117] = trackAuthEvent;
    $[118] = t37;
  } else {
    t37 = $[118];
  }
  const trackSignInMfaBackupCodeSelected = t37;
  let t38;
  if ($[119] !== baseProps || $[120] !== trackAuthEvent) {
    t38 = () => {
      trackAuthEvent(AUTH_EVENTS.SIGN_IN_COMPLETED, baseProps({
        flow: "sign_in",
        result: "success"
      }));
    };
    $[119] = baseProps;
    $[120] = trackAuthEvent;
    $[121] = t38;
  } else {
    t38 = $[121];
  }
  const trackSignInCompleted = t38;
  let t39;
  if ($[122] !== baseProps || $[123] !== trackAuthEvent) {
    t39 = redirectUrl => {
      trackAuthEvent(AUTH_EVENTS.AUTH_UNAUTHENTICATED_REDIRECTED, baseProps({
        flow: "sign_in",
        step: "start",
        ...(redirectUrl && {
          redirect_url: redirectUrl
        })
      }));
    };
    $[122] = baseProps;
    $[123] = trackAuthEvent;
    $[124] = t39;
  } else {
    t39 = $[124];
  }
  const trackAuthUnauthenticatedRedirected = t39;
  let t40;
  if ($[125] !== baseProps || $[126] !== identifyAuthUser || $[127] !== trackAuthError || $[128] !== trackAuthEvent || $[129] !== trackAuthScreenViewed || $[130] !== trackAuthUnauthenticatedRedirected || $[131] !== trackSignInCodeResent || $[132] !== trackSignInCompleted || $[133] !== trackSignInEmailReturnClicked || $[134] !== trackSignInForgotPasswordOpened || $[135] !== trackSignInMfaBackupCodeSelected || $[136] !== trackSignInMfaCompleted || $[137] !== trackSignInMfaStarted || $[138] !== trackSignInNewPasswordSubmitted || $[139] !== trackSignInResetCodeRequested || $[140] !== trackSignInResetCodeResent || $[141] !== trackSignInResetCodeSubmitted || $[142] !== trackSignInSessionSelected || $[143] !== trackSignInStarted || $[144] !== trackSignInStrategySelected || $[145] !== trackSignUpChoiceClicked || $[146] !== trackSignUpEmailCodeResent || $[147] !== trackSignUpEmailCodeSent || $[148] !== trackSignUpEmailReturnClicked || $[149] !== trackSignUpEmailVerified || $[150] !== trackSignUpExistingAccountHandoff || $[151] !== trackSignUpPasswordOptionToggled || $[152] !== trackSignUpSessionSelected || $[153] !== trackSignUpStarted || $[154] !== trackSignUpWebsiteCallbackArrived || $[155] !== trackSsoExistingSessionReused || $[156] !== trackSsoFallbackToSignIn || $[157] !== trackSsoFallbackToSignUp || $[158] !== trackSsoSignInCompleted || $[159] !== trackSsoSignUpCompleted || $[160] !== trackSsoSignUpRecoveryStarted || $[161] !== trackSsoTransferUsed) {
    t40 = {
      trackAuthEvent,
      trackAuthScreenViewed,
      trackSignInSessionSelected,
      trackSignUpSessionSelected,
      trackSignInForgotPasswordOpened,
      trackSignInResetCodeRequested,
      trackSignInResetCodeSubmitted,
      trackSignInResetCodeResent,
      trackSignInNewPasswordSubmitted,
      trackSignInCodeResent,
      trackSignUpChoiceClicked,
      trackSignUpEmailReturnClicked,
      trackSignUpStarted,
      trackSignUpPasswordOptionToggled,
      trackSignUpEmailCodeSent,
      trackSignUpEmailVerified,
      trackSignUpEmailCodeResent,
      trackSignUpExistingAccountHandoff,
      trackSignUpWebsiteCallbackArrived,
      trackSsoSignUpCompleted,
      trackSsoSignInCompleted,
      trackSsoTransferUsed,
      trackSsoExistingSessionReused,
      trackSsoFallbackToSignIn,
      trackSsoFallbackToSignUp,
      trackSsoSignUpRecoveryStarted,
      trackSignInStarted,
      trackSignInEmailReturnClicked,
      trackSignInMfaStarted,
      trackSignInMfaCompleted,
      trackSignInMfaBackupCodeSelected,
      trackSignInCompleted,
      trackSignInStrategySelected,
      trackAuthUnauthenticatedRedirected,
      trackAuthError,
      identifyAuthUser,
      baseProps
    };
    $[125] = baseProps;
    $[126] = identifyAuthUser;
    $[127] = trackAuthError;
    $[128] = trackAuthEvent;
    $[129] = trackAuthScreenViewed;
    $[130] = trackAuthUnauthenticatedRedirected;
    $[131] = trackSignInCodeResent;
    $[132] = trackSignInCompleted;
    $[133] = trackSignInEmailReturnClicked;
    $[134] = trackSignInForgotPasswordOpened;
    $[135] = trackSignInMfaBackupCodeSelected;
    $[136] = trackSignInMfaCompleted;
    $[137] = trackSignInMfaStarted;
    $[138] = trackSignInNewPasswordSubmitted;
    $[139] = trackSignInResetCodeRequested;
    $[140] = trackSignInResetCodeResent;
    $[141] = trackSignInResetCodeSubmitted;
    $[142] = trackSignInSessionSelected;
    $[143] = trackSignInStarted;
    $[144] = trackSignInStrategySelected;
    $[145] = trackSignUpChoiceClicked;
    $[146] = trackSignUpEmailCodeResent;
    $[147] = trackSignUpEmailCodeSent;
    $[148] = trackSignUpEmailReturnClicked;
    $[149] = trackSignUpEmailVerified;
    $[150] = trackSignUpExistingAccountHandoff;
    $[151] = trackSignUpPasswordOptionToggled;
    $[152] = trackSignUpSessionSelected;
    $[153] = trackSignUpStarted;
    $[154] = trackSignUpWebsiteCallbackArrived;
    $[155] = trackSsoExistingSessionReused;
    $[156] = trackSsoFallbackToSignIn;
    $[157] = trackSsoFallbackToSignUp;
    $[158] = trackSsoSignInCompleted;
    $[159] = trackSsoSignUpCompleted;
    $[160] = trackSsoSignUpRecoveryStarted;
    $[161] = trackSsoTransferUsed;
    $[162] = t40;
  } else {
    t40 = $[162];
  }
  return t40;
}